import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * QuizInteractionAnimation - 互动测验动画效果
 * 
 * 适用场景：
 * - 在线教育测验
 * - 知识点检测
 * - 课堂互动问答
 * - 培训考核
 * 
 * 教育价值评分：9.5/10
 * - 提高学习参与度
 * - 即时反馈学习效果
 * - 适合各类知识点测验
 */

interface QuizOption {
  text: string;
  isCorrect: boolean;
  explanation?: string;
}

interface QuizQuestion {
  question: string;
  options: QuizOption[];
  hint?: string;
}

export type QuizInteractionAnimationProps = {
  question?: QuizQuestion;
  showAnswerAfter?: number;
  primaryColor?: string;
  correctColor?: string;
  wrongColor?: string;
  backgroundColor?: string;
};

export const QuizInteractionAnimation: React.FC<QuizInteractionAnimationProps> = ({
  question = exampleQuizQuestion,
  showAnswerAfter = 90, // 默认 3 秒后显示答案（30fps）
  primaryColor = '#4A90E2',
  correctColor = '#10B981',
  wrongColor = '#EF4444',
  backgroundColor = '#0F1419',
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 问题出现动画
  const questionOpacity = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const questionY = interpolate(questionOpacity, [0, 1], [-30, 0]);

  // 选项逐个出现
  const optionDelay = 15; // 每个选项延迟 15 帧
  const showAnswer = frame >= showAnswerAfter;

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 60,
      }}
    >
      {/* 问题卡片 */}
      <div
        style={{
          width: width * 0.8,
          opacity: questionOpacity,
          transform: `translateY(${questionY}px)`,
        }}
      >
        {/* 问题类型标签 */}
        <div
          style={{
            display: 'inline-block',
            background: `linear-gradient(135deg, ${primaryColor} 0%, #7B68EE 100%)`,
            color: '#FFFFFF',
            padding: '8px 20px',
            borderRadius: 20,
            fontSize: 24,
            fontWeight: 600,
            marginBottom: 24,
            boxShadow: `0 4px 12px ${primaryColor}40`,
          }}
        >
          知识测验
        </div>

        {/* 问题文本 */}
        <h2
          style={{
            fontSize: 48,
            fontWeight: 700,
            color: '#FFFFFF',
            lineHeight: 1.3,
            marginBottom: 40,
            textShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {question.question}
        </h2>

        {/* 提示信息 */}
        {question.hint && frame < showAnswerAfter && (
          <div
            style={{
              background: 'rgba(255, 193, 7, 0.1)',
              border: '2px solid rgba(255, 193, 7, 0.3)',
              borderRadius: 12,
              padding: '12px 20px',
              marginBottom: 30,
              opacity: interpolate(frame, [60, 75], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }),
            }}
          >
            <span style={{ color: '#FFC107', fontSize: 24, fontWeight: 600 }}>
              💡 提示：{question.hint}
            </span>
          </div>
        )}

        {/* 选项 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 20,
            alignItems: 'start',
          }}
        >
          {question.options.map((option, index) => {
            const optionProgress = spring({
              frame: frame - (index * optionDelay + 20),
              fps,
              config: { damping: 12, stiffness: 80 },
            });

            const isCorrect = option.isCorrect;
            const isSelected = showAnswer && isCorrect;

            let optionColor = 'rgba(255, 255, 255, 0.05)';
            let borderColor = 'rgba(255, 255, 255, 0.2)';
            let glowColor = 'transparent';

            if (showAnswer) {
              if (isCorrect) {
                optionColor = 'rgba(16, 185, 129, 0.15)';
                borderColor = correctColor;
                glowColor = `${correctColor}40`;
              }
            }

            const scaleEffect = spring({
              frame: frame - showAnswerAfter,
              fps,
              config: { damping: 10, stiffness: 120 },
            });

            return (
              <div
                key={index}
                style={{
                  opacity: optionProgress,
                  transform: `translateX(${(1 - optionProgress) * (index % 2 === 0 ? -50 : 50)}px)`,
                  background: optionColor,
                  border: `3px solid ${borderColor}`,
                  borderRadius: 16,
                  padding: '24px 28px',
                  cursor: 'pointer',
                  boxShadow: isSelected ? `0 0 30px ${glowColor}` : '0 4px 16px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {/* 选项编号 */}
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: showAnswer && isCorrect
                        ? `linear-gradient(135deg, ${correctColor} 0%, #059669 100%)`
                        : 'rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 26,
                      fontWeight: 700,
                      color: '#FFFFFF',
                      flexShrink: 0,
                    }}
                  >
                    {String.fromCharCode(65 + index)}
                  </div>

                  {/* 选项文本 */}
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 500,
                      color: '#FFFFFF',
                      flex: 1,
                    }}
                  >
                    {option.text}
                  </div>

                  {/* 正确答案图标 */}
                  {showAnswer && isCorrect && (
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: correctColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24,
                        animation: 'pop 0.3s ease-out',
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>

                {/* 答案解释 */}
                {showAnswer && option.explanation && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 8,
                      fontSize: 20,
                      color: '#A0AEC0',
                      lineHeight: 1.5,
                      opacity: interpolate(frame, [showAnswerAfter + 10, showAnswerAfter + 25], [0, 1], {
                        extrapolateLeft: 'clamp',
                        extrapolateRight: 'clamp',
                      }),
                    }}
                  >
                    {option.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 倒计时或答案提示 */}
        {!showAnswer && (
          <div
            style={{
              marginTop: 40,
              textAlign: 'center',
              fontSize: 26,
              color: '#A0AEC0',
              opacity: interpolate(frame, [75, 90], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }),
            }}
          >
            ⏱️ 思考中...
          </div>
        )}

        {showAnswer && (
          <div
            style={{
              marginTop: 40,
              textAlign: 'center',
              fontSize: 26,
              fontWeight: 600,
              color: correctColor,
              opacity: spring({
                frame: frame - showAnswerAfter,
                fps,
                config: { damping: 12 },
              }),
            }}
          >
            ✨ 正确答案已揭晓！
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};

// 示例配置
export const exampleQuizQuestion: QuizQuestion = {
  question: '以下哪个是 JavaScript 中的原始数据类型？',
  hint: '原始数据类型是构成 JavaScript 值的基础',
  options: [
    {
      text: 'Object',
      isCorrect: false,
      explanation: 'Object 是引用类型，不是原始数据类型',
    },
    {
      text: 'String',
      isCorrect: true,
      explanation: 'String 是 JavaScript 的 7 种原始数据类型之一',
    },
    {
      text: 'Array',
      isCorrect: false,
      explanation: 'Array 本质上是特殊的 Object',
    },
    {
      text: 'Function',
      isCorrect: false,
      explanation: 'Function 是特殊的 Object 类型',
    },
  ],
};

// Composition 配置
export const quizInteractionAnimationConfig = {
  id: 'QuizInteractionAnimation',
  width: 1920,
  height: 1080,
  durationInFrames: 150,
  fps: 30,
  defaultProps: {
    question: exampleQuizQuestion,
    showAnswerAfter: 90,
    primaryColor: '#4A90E2',
    correctColor: '#10B981',
    wrongColor: '#EF4444',
    backgroundColor: '#0F1419',
  },
};
